import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { navItems, site } from "@/mocks/site";

function Logo() {
  return (
    <Link to="/" className="flex items-center gap-3 group cursor-pointer">
      <span className="w-10 h-10 flex items-center justify-center rounded-md bg-accent-500 text-background-50 font-heading font-bold text-lg">
        T4
      </span>
      <span className="flex flex-col leading-none">
        <span className="font-heading font-semibold text-foreground-950 tracking-wide text-[15px] group-hover:text-accent-400 transition-colors">
          {site.name}
        </span>
        <span className="font-label text-[10px] tracking-[0.28em] text-foreground-500 mt-1">TABOO IV</span>
      </span>
    </Link>
  );
}

function SearchBox({ onDone }: { onDone?: () => void }) {
  const navigate = useNavigate();
  const [value, setValue] = useState("");

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const q = value.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : "/search");
    if (onDone) onDone();
  };

  return (
    <form onSubmit={submit} className="relative">
      <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-500 text-sm" />
      <input
        type="search"
        name="q"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder="搜索剧集 / 角色 / 资讯"
        className="w-full rounded-md bg-background-50 border border-background-200 pl-9 pr-3 py-2 text-sm text-foreground-900 placeholder:text-foreground-500 focus:outline-none focus:border-accent-500/60 transition-colors"
      />
    </form>
  );
}

export default function Sidebar() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const navList = (
    <nav className="flex flex-col gap-1">
      {navItems.map((item) => (
        <NavLink
          key={item.key}
          to={item.path}
          end={item.path === "/"}
          className={({ isActive }) =>
            `group flex items-center gap-3 rounded-md px-3 py-2.5 text-sm transition-colors cursor-pointer ${
              isActive
                ? "bg-accent-500/12 text-accent-400 border border-accent-500/25"
                : "text-foreground-600 hover:bg-background-200/60 hover:text-foreground-900 border border-transparent"
            }`
          }
        >
          <span className="w-6 h-6 flex items-center justify-center">
            <i className={`${item.icon} text-lg`} />
          </span>
          <span className="font-label tracking-wide">{item.label}</span>
        </NavLink>
      ))}
    </nav>
  );

  const asideInner = (
    <>
      <div className="px-5 pt-6 pb-5">
        <Logo />
      </div>
      <div className="px-5 pb-5">
        <SearchBox />
      </div>
      <div className="px-3 flex-1 overflow-y-auto">{navList}</div>
      <div className="px-5 py-5 border-t border-background-200">
        <p className="font-label text-[10px] tracking-[0.2em] text-foreground-500">SEASONS 01 — 04</p>
        <p className="mt-2 text-xs text-foreground-500 leading-relaxed">
          四季主线 · 共 20 集<br />剧情解析 · 角色档案 · 最新资讯
        </p>
      </div>
    </>
  );

  return (
    <>
      {/* Mobile top bar */}
      <header className="lg:hidden sticky top-0 z-40 bg-background-100/95 backdrop-blur border-b border-background-200">
        <div className="flex items-center justify-between px-4 h-16">
          <Logo />
          <button
            type="button"
            onClick={() => setOpen(true)}
            aria-label="打开菜单"
            className="w-10 h-10 flex items-center justify-center rounded-md border border-background-200 text-foreground-800 hover:bg-background-200/60 transition-colors cursor-pointer"
          >
            <i className="ri-menu-3-line text-xl" />
          </button>
        </div>
      </header>

      {/* Desktop sidebar */}
      <aside className="hidden lg:flex flex-col fixed left-0 top-0 h-screen w-[260px] bg-background-100 border-r border-background-200 z-30">
        {asideInner}
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div
            className="absolute inset-0 bg-black/60 animate-fade-in"
            onClick={() => setOpen(false)}
            aria-hidden="true"
          />
          <div className="absolute left-0 top-0 h-full w-[80%] max-w-[320px] bg-background-100 border-r border-background-200 flex flex-col">
            <div className="flex items-center justify-between px-5 pt-5 pb-4">
              <Logo />
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="关闭菜单"
                className="w-9 h-9 flex items-center justify-center rounded-md border border-background-200 text-foreground-700 cursor-pointer"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <div className="px-5 pb-4">
              <SearchBox onDone={() => setOpen(false)} />
            </div>
            <div className="px-3 flex-1 overflow-y-auto">{navList}</div>
            <div className="px-5 py-5 border-t border-background-200">
              <p className="font-label text-[10px] tracking-[0.2em] text-foreground-500">SEASONS 01 — 04</p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}